import React from 'react';
import { useQuery } from 'react-query';
import { getIncomeGain } from 'config/api';
import { formatNumber, formatDateForString } from 'utils/formatter';
import {
  IncomeGainItemProps,
  IncomeGainProps,
  getSummaryIncomeGainAmount,
} from 'pages/DividendDeposit';
import {
  getSummaryIncomeGain,
  getSortIncomeGainAmountData,
} from 'pages/DividendDetailDeposit';
import { DividendDepositLoading } from 'components/features/dividendDeposit/DividendError';
import { DividendPieChart } from 'components/features/dividendDeposit/DividendChart';
import Loading from 'components/commons/loading/Loading';
import { TitleProps } from './TargetAchievementList';
import { DividendList } from './DividendList';
import { yyyymmddToDate } from '../../daily-report/DailyReport';

const DividendDataLoading = () => {
  return (
    <div className="bg-white">
      <Loading
        loading={true}
        circularLoading={true}
        style={{
          paddingTop: '60px',
          paddingBottom: '60px',
        }}
      />
    </div>
  );
};

const NoDividendData = () => {
  return (
    <div className="bg-white p-6">
      <div className="p-6 text-sm text-bluegray-500 bg-bluegray-50 text-center border border-bluegray-300 rounded-lg">
        受け取った配当金はありません
      </div>
    </div>
  );
};

const Title: React.FC<TitleProps> = ({ month }) => (
  <div className="h-22.5 flex items-end p-4 text-lg font-bold text-bluegray-700 leading-tight">
    <div className="icon-dividend text-xl mr-2" />
    {month}月の配当金
  </div>
);

const ToDividend = () => {
  const link = '/dividenddeposit';
  return (
    <div className="p-4 overflow-hidden">
      <div className="w-full flex items-center">
        <a
          className="h-5 flex items-center ml-auto text-sm text-blue-700"
          href={link}>
          <div className="icon-right"></div>
          配当金管理へ
        </a>
      </div>
    </div>
  );
};

const DividendInternal = ({
  incomeGain,
}: {
  incomeGain: IncomeGainItemProps[];
}) => {
  const summaryIncomeGain = getSummaryIncomeGain(incomeGain);
  const sortedTargetIncomeGain = getSortIncomeGainAmountData(summaryIncomeGain);
  const totalDividend = getSummaryIncomeGainAmount(sortedTargetIncomeGain);

  if (
    sortedTargetIncomeGain === null ||
    sortedTargetIncomeGain === undefined ||
    totalDividend === null
  )
    return <DividendDepositLoading str={'銘柄'} />;

  return (
    <>
      <div className="bg-white text-bluegray-700">
        <div className="pt-6 text-sm font-bold text-center">入金合計</div>
        <div className="px-4 text-center text-xs">
          <span className="mr-1 text-2xl font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>

        <div className="mt-2 mb-6 mx-1">
          <DividendPieChart {...{ incomeGainData: sortedTargetIncomeGain }} />
        </div>
        <DividendList
          {...{
            incomeGainData: sortedTargetIncomeGain,
          }}
        />
      </div>
    </>
  );
};

export const Dividend = ({
  from,
  queryPeriod,
}: {
  from: number;
  queryPeriod: {
    from: string;
    to: string;
  };
}) => {
  const month = (yyyymmddToDate(from.toString()) || new Date()).getMonth() + 1;

  const { data: incomeGain } = useQuery(
    'monthlyReportIncomeGain',
    () => getIncomeGainAPI(queryPeriod.from, queryPeriod.to),
    {
      refetchOnWindowFocus: false,
    }
  );

  return (
    <>
      <Title month={month} />
      {incomeGain === null || incomeGain === undefined ? (
        <DividendDataLoading />
      ) : (
        <>
          {incomeGain.length === 0 ? (
            <NoDividendData />
          ) : (
            <DividendInternal {...{ incomeGain }} />
          )}
        </>
      )}
      <ToDividend />
    </>
  );
};

const getIncomeGainAPI = async (
  from: string,
  to: string
): Promise<IncomeGainItemProps[]> => {
  const queryFrom = formatDateForString(from, 'yyyyMM');
  const queryTo = formatDateForString(to, 'yyyyMM');

  const ret_ = await getIncomeGain(queryFrom, queryTo);
  const ret = {
    from: ret_.From,
    to: ret_.To,
    items: ret_.Items.map(item => {
      return {
        account: item.Account,
        brokerID: item.BrokerID,
        brokerName: item.BrokerName,
        symbol: item.Symbol,
        symbolName: item.SymbolName,
        productType: item.ProductType,
        amount: item.Amount,
        date: item.Date,
        isin: item.ISIN,
        hasNew: item.HasNew,
      };
    }) as IncomeGainItemProps[],
  } as IncomeGainProps;

  return ret.items;
};
